import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { NgTemplateOutlet } from '@angular/common';
import {
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { filter } from 'rxjs';
import { LayoutStore } from '@twist/ui-core';
import { SvgIconComponent, SvgIcons } from '@twist/ui/components';
import { AuthGuardDirective } from '@twist/core';
import {
  TwPopoverComponent,
  TwPopoverContentComponent,
  TwPopoverTriggerDirective,
  TwTooltipDirective,
} from '@twist/ui-popovers';

import { TwBadgeComponent, TwBadgeStatus } from '@twist/ui-primitives';
import {
  ActiveTrailResolver,
  NavBadge,
  NavBadgeProvider,
  NavItem,
  NavRegistry,
  NavSource,
  NAV_BADGE_PROVIDER,
  NAV_SOURCE,
} from '@twist/ui-navigation';

import { TWIST_LAYOUT_ENVIRONMENT } from '../../tokens/layout-environment.token';
import { SideNavSection, SideNavTopLevelItem } from './sidebar-nav.models';

/**
 * Sidebar navigation — rail / full / hidden modes.
 *
 * TWS-108 Wave 8 (Portal Chrome Restyle): the bespoke rail-mode
 * flyout (cdkConnectedOverlay + manual timers + `popoutHover`
 * signal) was retired in favour of the shared `<tw-popover>`
 * primitive (TWS-122). The primitive owns its own hover-state
 * machine with separate "trigger hovered" / "panel hovered"
 * flags — which removes the sticky-hover bug where the flyout
 * stayed visible when the cursor exited the rail directly into
 * main content without crossing the panel.
 *
 * Each group-parent now wraps its trigger in its own
 * `<tw-popover>` (enabled only in rail mode or when the group
 * is collapsed in full mode); leaf rail items use `[twTooltip]`
 * for the label-only tooltip. The portal-level `cdkConnectedOverlay`
 * pattern, the `popoutHover` signal, and the open/close timer
 * fields are all gone.
 */
@Component({
  standalone: true,
  selector: 'app-sidebar-nav',
  imports: [
    NgTemplateOutlet,
    RouterLink,
    RouterLinkActive,
    SvgIconComponent,
    AuthGuardDirective,
    TwPopoverComponent,
    TwPopoverContentComponent,
    TwPopoverTriggerDirective,
    TwTooltipDirective,
    TwBadgeComponent,
  ],
  templateUrl: './sidebar-nav.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'tw-layout__sidebar',
  },
})
export class SidebarNavComponent {
  private readonly layoutStore = inject(LayoutStore);
  private router = inject(Router);

  /**
   * Ordered set of navigation sources (RegistryNavSource = #1, then any
   * personalised sources). Injected `optional` so the sidebar still
   * works in contexts that provide none (legacy-only fallback). Each
   * source's `items()` is a Signal so `composedSections` stays reactive.
   */
  private readonly navSources =
    (inject(NAV_SOURCE, { optional: true }) as unknown as NavSource[] | null) ?? [];
  private readonly navRegistry = inject(NavRegistry);
  private readonly activeTrail = inject(ActiveTrailResolver);
  private readonly destroyRef = inject(DestroyRef);
  private readonly badgeProviders =
    (inject(NAV_BADGE_PROVIDER, { optional: true }) as unknown as NavBadgeProvider[] | null) ??
    [];

  /**
   * Memoised per-navId badge signals — resolved once per id. Each is the
   * first non-null provider override for that id (reactive); the static
   * descriptor badge is the base used when no provider overrides.
   */
  private readonly _providerBadgeCache = new Map<string, () => NavBadge | null>();

  /**
   * The owning navId of the current route, resolved on every
   * NavigationEnd via the ActiveTrailResolver (deepest snapshot →
   * nearest `data.navId` → descriptor). Drives the active highlight for
   * registry-backed items on deep/detail routes that `Router.isActive`
   * (exact-match) would miss — e.g. `/installations/:id/diagnostics`
   * still lights up the `installations` sidebar item.
   */
  readonly activeNavId = signal<string | undefined>(undefined);

  constructor() {
    // Seed from the current route, then track NavigationEnd. Walking to
    // the deepest snapshot lets a detail route's `data.navId` resolve
    // even when the sidebar link is several segments shallower.
    this.recomputeActiveNavId();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.recomputeActiveNavId());
  }

  private recomputeActiveNavId(): void {
    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (snapshot?.firstChild) {
      snapshot = snapshot.firstChild;
    }
    this.activeNavId.set(this.activeTrail.navIdFor(snapshot));
  }

  /**
   * Whether a registry-composed item should render as active. Combines
   * the active-trail navId match (covers deep/detail routes) with the
   * existing exact-link check (covers the shallow case + the very first
   * paint before NavigationEnd fires). Legacy items keep their own
   * `isActiveLink` / routerLinkActive logic untouched.
   */
  isRegistryItemActive(item: SideNavTopLevelItem): boolean {
    if (item.navId && item.navId === this.activeNavId()) {
      return true;
    }
    return this.isActiveLink(item.routerLink);
  }

  /**
   * The effective badge for a registry-composed LEAF item: a non-null
   * NAV_BADGE_PROVIDER result for its navId overrides the static
   * descriptor badge reactively; otherwise the static badge applies.
   * Returns null when neither yields a badge. Legacy items have no
   * badge (return null) — there is NO group/section aggregation.
   */
  effectiveBadge(item: SideNavTopLevelItem): NavBadge | null {
    if (!item.navId) {
      return null;
    }
    const override = this._providerBadge(item.navId)();
    return override ?? item.badge ?? null;
  }

  /** tw-badge `type` for a NavBadge: status-count → status, else tag. */
  badgeType(badge: NavBadge): 'status' | 'tag' {
    return badge.kind === 'status-count' ? 'status' : 'tag';
  }

  /** tw-badge `status` tone for a status-count badge (neutral otherwise). */
  badgeStatus(badge: NavBadge): TwBadgeStatus {
    return badge.kind === 'status-count' ? badge.severity : 'neutral';
  }

  /**
   * Rendered label for a NavBadge:
   *  - count        → the value, or `${cap}+` when value exceeds cap.
   *  - text         → the text verbatim.
   *  - status-count → the value (tone carried by `badgeStatus`).
   */
  badgeLabel(badge: NavBadge): string {
    switch (badge.kind) {
      case 'count':
        return badge.cap !== undefined && badge.value > badge.cap
          ? `${badge.cap}+`
          : `${badge.value}`;
      case 'text':
        return badge.text;
      case 'status-count':
        return `${badge.value}`;
    }
  }

  private _providerBadge(navId: string): () => NavBadge | null {
    let resolver = this._providerBadgeCache.get(navId);
    if (!resolver) {
      const signals = this.badgeProviders.map((p) => p.badgeFor(navId));
      resolver = () => {
        for (const s of signals) {
          const value = s();
          if (value) {
            return value;
          }
        }
        return null;
      };
      this._providerBadgeCache.set(navId, resolver);
    }
    return resolver;
  }

  /** Public readable signal — drives template bindings for rail/full/hidden branches. */
  readonly sidebarState = this.layoutStore.effectiveSidebarState;

  /** True when the sidebar is in rail mode — drives brand logo variant + flyout eligibility. */
  readonly isRail = computed(() => this.layoutStore.effectiveSidebarState() === 'rail');

  /**
   * Registry-composed sidebar sections — the SOLE source of sidebar
   * content (the static sideNavConfig + legacy fallback were retired in
   * Phase 4). Built from the ordered NAV_SOURCE set (RegistryNavSource =
   * #1): every source's NavItems are flattened, grouped by their owning
   * descriptor's `group`, and projected onto the SideNavSection shape.
   *
   * Each composed top-level item carries `securityConstraints` resolved
   * from `descriptor(navId).security`, so the existing per-item
   * `*authGuard` gate (the REAL async visibility check) applies
   * identically to registry items. Items the user can't access never
   * enter the DOM. `canAccess()` on the NavItem is NOT consulted here.
   */
  readonly composedSections = computed<SideNavSection[]>(() => {
    const items: NavItem[] = this.navSources.flatMap((s) => s.items()());
    const byGroup = new Map<string, SideNavTopLevelItem[]>();
    // Lowest `order` seen per group — drives the SECTION ordering so the
    // sequence (My Twist → Organization → Analytics → Admin) follows the
    // descriptors' explicit `order`, not the registry's alphabetical
    // group sort. A group whose items all lack `order` sinks to the end.
    const groupMinOrder = new Map<string, number>();
    // Per-item order, kept parallel to byGroup so we can stable-sort the
    // items WITHIN a group without mutating the SideNavTopLevelItem shape.
    const itemOrder = new Map<SideNavTopLevelItem, number>();
    for (const item of items) {
      const descriptor = this.navRegistry.descriptor(item.id);
      const group = descriptor?.group ?? '';
      if (!byGroup.has(group)) {
        byGroup.set(group, []);
      }
      const order = item.order ?? Number.MAX_SAFE_INTEGER;
      const topLevel: SideNavTopLevelItem = {
        title: item.label,
        icon: item.icon ?? '',
        // A group descriptor (children present) is rendered as an
        // expandable parent: it must NOT carry a routerLink (the template
        // keys the leaf-vs-group branch off `hasSubItems`). Its sub-items
        // own the navigation.
        routerLink: item.subItems?.length ? undefined : item.target,
        securityConstraints: descriptor?.security,
        navId: item.id,
        badge: item.badge,
        external: descriptor?.external,
        disabled: descriptor?.disabled,
        // Project the resolved sub-items, re-resolving each child's
        // descriptor.security so the per-sub-item `*authGuard` gate fires
        // exactly as the legacy fallback did.
        subItems: item.subItems?.map((sub) => ({
          title: sub.label,
          routerLink: sub.target,
          securityConstraints: this.navRegistry.descriptor(sub.id)?.security,
        })),
      };
      itemOrder.set(topLevel, order);
      byGroup.get(group)!.push(topLevel);
      const currentMin = groupMinOrder.get(group) ?? Number.MAX_SAFE_INTEGER;
      if (order < currentMin) {
        groupMinOrder.set(group, order);
      }
    }
    // Sort groups by their minimum item order (ties → alphabetical), and
    // each group's items by their own order (ties → alphabetical label).
    const groups = [...byGroup.keys()].sort((a, b) => {
      const orderA = groupMinOrder.get(a) ?? Number.MAX_SAFE_INTEGER;
      const orderB = groupMinOrder.get(b) ?? Number.MAX_SAFE_INTEGER;
      return orderA !== orderB ? orderA - orderB : a.localeCompare(b);
    });
    return groups.map<SideNavSection>((group) => ({
      title: group,
      items: [...byGroup.get(group)!].sort((a, b) => {
        const orderA = itemOrder.get(a)!;
        const orderB = itemOrder.get(b)!;
        return orderA !== orderB ? orderA - orderB : a.title.localeCompare(b.title);
      }),
    }));
  });

  /**
   * Reserved "Pinned" personalisation slot, rendered after the
   * registry-composed sections. Empty for now (no PinnedNavSource yet) —
   * the template renders nothing when its items list is empty, holding
   * the layout position for a future wave.
   */
  readonly pinnedSection = computed<SideNavSection>(() => ({
    title: 'Pinned',
    items: [],
  }));

  toggleSidebar() {
    this.layoutStore.cycleSidebarState();
  }

  protected readonly layoutEnvironment = inject(TWIST_LAYOUT_ENVIRONMENT);

  hasSubItems(topLevelItem: SideNavTopLevelItem) {
    return !!topLevelItem.subItems && topLevelItem.subItems.length > 0;
  }

  /**
   * Returns `true` when the current route matches one of the group's
   * sub-items. Drives the `.tw-sidebar__item--active` class on the
   * group-parent button — design handoff marks the parent active
   * whenever a child is active, NOT when the group is just expanded
   * (the Wave 4 logic mistakenly equated "expanded" with "active").
   */
  hasActiveChild(topLevelItem: SideNavTopLevelItem): boolean {
    if (!topLevelItem.subItems) {
      return false;
    }
    return topLevelItem.subItems.some(sub => this.isActiveLink(sub.routerLink));
  }

  asSvgIcon(icon: string): keyof { [key in SvgIcons]: string } {
    return icon as keyof { [key in SvgIcons]: string };
  }

  isActiveLink(routerLink?: string) {
    if (!routerLink) {
      return false;
    }
    return this.router.isActive(routerLink, {
      paths: 'exact',
      queryParams: 'ignored',
      fragment: 'ignored',
      matrixParams: 'ignored'
    });
  }

  isCollapsed(key: string) {
    return computed(() => !this.layoutStore.openGroups().includes(key));
  }

  /**
   * Whether the flyout trigger for a given group should be enabled.
   * The popover should only open in rail mode (full mode shows the
   * subnav inline) or when the group is collapsed in full mode (the
   * Wave 4 affordance — hover a collapsed group to peek its children).
   */
  flyoutEnabled(collapsed: boolean): boolean {
    return this.isRail() || collapsed;
  }

  toggleCollapse(title: string) {
    this.layoutStore.toggleGroup(title);
  }
}
